import { useEffect } from "react";

/**
 * Darstellung „Automatisch“ (oberflaeche.md §7, design/README §2.4, Tokens `regel.automatik.*`): Dämmerung nach der
 * Sonnenhöhe am Ort der Drückjagd, nicht nach `prefers-color-scheme`. Unter +4° Dämmerung, über +6° wieder Tag
 * (Hysterese), frühestens 10 min nach dem letzten Wechsel. Offline aus Datum und Ort berechnet; Ort ist die zuletzt
 * gemessene Geräteposition (am Stand gemessen, LIM-0009: nie im Hintergrund), sonst die Lage der Drückjagd (Mitte ihrer
 * Rettungspunkte). Umgeschaltet wird nur beim Start, in den Einstellungen und auf der Startansicht, nie in einem Ablauf
 * oder während einer Meldung.
 */
export type Modus = "tag" | "daemmerung";
export type Darstellung = "Automatisch" | "Tag" | "Dämmerung";

export interface Ort {
  Breite: number;
  Länge: number;
}

export interface Automatik {
  modus: Modus;
  /** Zeitpunkt des letzten Wechsels (ms seit 1970) */
  seit: number;
}

export interface Regel {
  daemmerungUnterGrad: number;
  tagUeberGrad: number;
  mindestabstandMs: number;
}

export const DARSTELLUNG_SCHLUESSEL = "dj-darstellung";
const AUTOMATIK_SCHLUESSEL = "dj-darstellung-automatik";
const ORT_SCHLUESSEL = "dj-darstellung-ort";

/**
 * Ohne bekannten Ort (vor der ersten Drückjagd auf diesem Gerät) gilt die Mitte Deutschlands; die Dämmerungsgrenze
 * weicht dort innerhalb Deutschlands höchstens um etwa eine halbe Stunde ab.
 */
export const ORT_OHNE_DRUECKJAGD: Ort = { Breite: 51.16, Länge: 10.45 };

const GRAD = Math.PI / 180;

/**
 * Sonnenhöhe in Grad (geometrisch, ohne Refraktion) nach den Näherungsformeln des Astronomical Almanac (Genauigkeit
 * etwa 0,01° zwischen 1950 und 2050).
 */
export function sonnenhoehe(zeit: Date, ort: Ort): number {
  const n = zeit.getTime() / 86_400_000 + 2_440_587.5 - 2_451_545.0;
  const mittlereLaenge = rest(280.46 + 0.9856474 * n, 360);
  const anomalie = rest(357.528 + 0.9856003 * n, 360) * GRAD;
  const ekliptik = (mittlereLaenge + 1.915 * Math.sin(anomalie) + 0.02 * Math.sin(2 * anomalie)) * GRAD;
  const schiefe = (23.439 - 0.0000004 * n) * GRAD;
  const rektaszension = Math.atan2(Math.cos(schiefe) * Math.sin(ekliptik), Math.cos(ekliptik));
  const deklination = Math.asin(Math.sin(schiefe) * Math.sin(ekliptik));
  const sternzeit = rest(280.46061837 + 360.98564736629 * n + ort.Länge, 360) * GRAD;
  const stundenwinkel = sternzeit - rektaszension;
  const breite = ort.Breite * GRAD;
  const sinH = Math.sin(breite) * Math.sin(deklination) + Math.cos(breite) * Math.cos(deklination) * Math.cos(stundenwinkel);
  return Math.asin(Math.max(-1, Math.min(1, sinH))) / GRAD;
}

function rest(x: number, m: number): number {
  return ((x % m) + m) % m;
}

/** Nächster Modus aus der Sonnenhöhe: Hysterese zwischen den Grenzen und Mindestabstand zwischen zwei Wechseln. */
export function automatik(vorher: Automatik | null, hoehe: number, jetzt: number, regel: Regel): Automatik {
  const ziel: Modus = hoehe < regel.daemmerungUnterGrad ? "daemmerung"
    : hoehe > regel.tagUeberGrad ? "tag"
      : vorher?.modus ?? (hoehe < (regel.daemmerungUnterGrad + regel.tagUeberGrad) / 2 ? "daemmerung" : "tag");
  if (!vorher) return { modus: ziel, seit: jetzt };
  if (ziel === vorher.modus) return vorher;
  // Eine zurückgestellte Geräteuhr (seit in der Zukunft) hält den Wechsel nicht auf
  if (jetzt >= vorher.seit && jetzt - vorher.seit < regel.mindestabstandMs) return vorher;
  return { modus: ziel, seit: jetzt };
}

/**
 * Beginn der Dämmerung am Tag von `zeit` (erster Zeitpunkt nach dem höchsten Sonnenstand, an dem die Sonne unter die
 * Grenze sinkt), minutengenau; `null`, wenn die Sonne an diesem Tag nicht unter die Grenze sinkt.
 */
export function daemmerungAb(zeit: Date, ort: Ort, regel: Regel): Date | null {
  // Ortsmittag in UTC: 12:00 minus 4 min je Grad östlicher Länge
  const mittag = Date.UTC(zeit.getUTCFullYear(), zeit.getUTCMonth(), zeit.getUTCDate(), 12) - ort.Länge * 4 * 60_000;
  for (let m = 0; m <= 12 * 60; m += 1) {
    const t = new Date(mittag + m * 60_000);
    if (sonnenhoehe(t, ort) < regel.daemmerungUnterGrad) return t;
  }
  return null;
}

/** Lage der Drückjagd: Mitte ihrer Rettungspunkte (lesemodelle.md §3, offline vorhanden); ohne gültige Punkte `null`. */
export function ortAusRettungspunkten(punkte: ReadonlyArray<Record<string, unknown>>): Ort | null {
  const gueltig = punkte.map((p) => ({ Breite: Number(p["Breite"]), Länge: Number(p["Länge"]) }))
    .filter((p) => Number.isFinite(p.Breite) && Number.isFinite(p.Länge) && Math.abs(p.Breite) <= 90 && Math.abs(p.Länge) <= 180);
  if (gueltig.length === 0) return null;
  return {
    Breite: gueltig.reduce((s, p) => s + p.Breite, 0) / gueltig.length,
    Länge: gueltig.reduce((s, p) => s + p.Länge, 0) / gueltig.length,
  };
}

/** Grenzen aus den Tokens (`--dj-regel-automatik-*`), damit eine Änderung im Designsystem ohne Code wirkt. */
export function regelAusTokens(): Regel {
  const stil = getComputedStyle(document.documentElement);
  const zahl = (name: string, vorgabe: number) => {
    const w = Number.parseFloat(stil.getPropertyValue(`--dj-regel-automatik-${name}`));
    return Number.isFinite(w) ? w : vorgabe;
  };
  return {
    daemmerungUnterGrad: zahl("daemmerung-unter-grad", 4),
    tagUeberGrad: zahl("tag-ueber-grad", 6),
    mindestabstandMs: zahl("mindestabstand-min", 10) * 60_000,
  };
}

function lesen<T>(schluessel: string): T | null {
  try {
    const w = localStorage.getItem(schluessel);
    return w ? (JSON.parse(w) as T) : null;
  } catch {
    return null;
  }
}

export function gewaehlteDarstellung(): Darstellung {
  const w = localStorage.getItem(DARSTELLUNG_SCHLUESSEL);
  return w === "Tag" || w === "Dämmerung" ? w : "Automatisch";
}

/** Ort, nach dem „Automatisch“ rechnet (zuletzt bekannter Ort der Drückjagd, sonst die Vorgabe). */
export function ortDerAutomatik(): Ort {
  return lesen<Ort>(ORT_SCHLUESSEL) ?? ORT_OHNE_DRUECKJAGD;
}

function modusSetzen(modus: Modus): void {
  if (document.documentElement.dataset["modus"] !== modus) document.documentElement.dataset["modus"] = modus;
}

/**
 * Setzt `data-modus` nach der gewählten Darstellung; bei „Automatisch“ nach der Sonnenhöhe am gemerkten Ort (beim Start,
 * in den Einstellungen und auf der Startansicht).
 */
export function darstellungAnwenden(wert: string | null = gewaehlteDarstellung(), jetzt = Date.now()): void {
  if (wert === "Tag" || wert === "Dämmerung") {
    modusSetzen(wert === "Dämmerung" ? "daemmerung" : "tag");
    return;
  }
  const neu = automatik(lesen<Automatik>(AUTOMATIK_SCHLUESSEL), sonnenhoehe(new Date(jetzt), ortDerAutomatik()), jetzt, regelAusTokens());
  localStorage.setItem(AUTOMATIK_SCHLUESSEL, JSON.stringify(neu));
  modusSetzen(neu.modus);
}

/** Merkt den Ort der Drückjagd für „Automatisch“ (auf eine Nachkommastelle gerundet, etwa 10 km: genug für die Sonne). */
export function ortMerken(ort: Ort): void {
  const gerundet = { Breite: Math.round(ort.Breite * 10) / 10, Länge: Math.round(ort.Länge * 10) / 10 };
  localStorage.setItem(ORT_SCHLUESSEL, JSON.stringify(gerundet));
}

const PRUEFABSTAND_MS = 60_000;

/**
 * Auf der Startansicht: merkt den Ort und prüft „Automatisch“ beim Öffnen und jede Minute, solange `erlaubt` (keine
 * Meldung offen).
 */
export function useAutomatik(ort: Ort | null, erlaubt: boolean): void {
  const breite = ort?.Breite;
  const laenge = ort?.Länge;
  useEffect(() => {
    if (breite !== undefined && laenge !== undefined) ortMerken({ Breite: breite, Länge: laenge });
  }, [breite, laenge]);
  useEffect(() => {
    if (!erlaubt || gewaehlteDarstellung() !== "Automatisch") return;
    darstellungAnwenden("Automatisch");
    const t = window.setInterval(() => darstellungAnwenden(), PRUEFABSTAND_MS);
    return () => window.clearInterval(t);
  }, [erlaubt, breite, laenge]);
}
